import { motion } from 'framer-motion'
import { Link, useParams } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { articles, getArticleByLocaleSlug, articleSlug } from '../data/articles'
import {
  isArticleContentReady,
  loadArticleContent,
  readArticleHtml,
} from '../data/articleContentStore'
import { localizeHtmlLinks, formatDateLocalized } from '../lib/localizeHtml'
import ArticleCover from '../components/blog/ArticleCover'
import {
  orgSchema,
  authorSchema,
  breadcrumbSchema,
  faqPageSchema,
  clampMeta,
} from '../lib/seo'
import { AUTHOR } from '../lib/links'
import SeoHead from '../components/SeoHead'
import { useT } from '../i18n/LocaleContext'
import { localizedPath, dynamicAlternates } from '../i18n/routes'

export default function BlogArticlePage() {
  const { slug } = useParams()
  const { t, locale } = useT()

  const article = useMemo(
    () => (slug ? getArticleByLocaleSlug(locale, slug) : undefined),
    [locale, slug],
  )
  // Au prérendu le contenu est déjà injecté (entry-server) : `ready` vaut true
  // et le corps part dans le HTML. À l'hydratation il vaut false le temps que
  // le chunk arrive — le corps prérendu reste en place, React ne réécrit pas
  // l'innerHTML d'un nœud hydraté.
  const [ready, setReady] = useState(isArticleContentReady)

  useEffect(() => {
    if (ready) return
    let alive = true
    loadArticleContent().then(() => {
      if (alive) setReady(true)
    })
    return () => {
      alive = false
    }
  }, [ready])

  const content = useMemo(() => {
    if (!article || !ready) return ''
    const raw = readArticleHtml(locale, article.id)
    return raw === undefined ? '' : localizeHtmlLinks(raw, locale)
  }, [article, locale, ready])

  // Related articles (same category key, excluding self)
  const related = useMemo(() => {
    if (!article) return []
    return articles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 3)
  }, [article])

  if (!article) {
    return (
      <main className="relative pt-32 pb-24 max-w-3xl mx-auto px-6 text-center">
        <h1 className="text-3xl font-semibold text-ink-950">
          {t('blog.article.notFoundTitle')}
        </h1>
        <Link
          to={localizedPath('blog', locale)}
          className="mt-6 inline-block text-accent-700 hover:text-accent-800"
        >
          {t('blog.article.notFoundLink')}
        </Link>
      </main>
    )
  }

  const meta = article.i18n[locale]
  const categoryLabel = t('blog.categories.' + article.category)
  const formattedDate = formatDateLocalized(article.date, locale)
  const articlePath = localizedPath('blog.article', locale, { slug: meta.slug })
  const articleUrl = `https://yaslan.be${articlePath}`

  return (
    <>
    <SeoHead
      title={`${meta.seoTitle ?? meta.title} — Yaslan`}
      description={clampMeta(meta.excerpt)}
      path={articlePath}
      alternates={dynamicAlternates('blog.article', {
        fr: article.i18n.fr.slug,
        en: article.i18n.en.slug,
        nl: article.i18n.nl.slug,
      })}
      jsonLd={[
        {
          '@type': 'BlogPosting',
          '@id': `${articleUrl}#post`,
          inLanguage: locale,
          headline: meta.title,
          description: clampMeta(meta.excerpt),
          datePublished: article.date,
          dateModified: article.dateModified ?? article.date,
          // A named Person, not the organization — the byline rendered on the
          // page must match what the markup claims.
          author: { '@id': AUTHOR.id },
          publisher: { '@id': 'https://yaslan.be/#organization' },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': articleUrl,
          },
          url: articleUrl,
          image: `https://yaslan.be/og-default.png`,
          articleSection: categoryLabel,
        },
        authorSchema(),
        orgSchema(locale),
        breadcrumbSchema([
          { name: t('breadcrumb.home'), path: localizedPath('home', locale) },
          { name: t('breadcrumb.blog'), path: localizedPath('blog', locale) },
          { name: meta.title, path: articlePath },
        ]),
        // Only for articles that declare a FAQ block; the questions mirror the
        // <h3> actually rendered in the body (Google requires visible parity).
        ...(meta.faq?.length
          ? [
              faqPageSchema(meta.faq, {
                id: `${articleUrl}#faq`,
                inLanguage: locale,
              }),
            ]
          : []),
      ]}
    />
    <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
      {/* Background DA */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[600px] -z-10 pointer-events-none"
      >
        <div
          className="absolute inset-x-0 -top-20 h-[600px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.18), rgba(59,93,255,0.05) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.22]"
          style={{
            backgroundImage:
              'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage:
              'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 sm:px-6">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('blog.article.breadcrumbHome')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <Link to={localizedPath('blog', locale)} className="hover:text-accent-700 transition-colors">
            {t('blog.article.breadcrumbBlog')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700 line-clamp-1 inline-block max-w-[200px] sm:max-w-none align-bottom">
            {meta.title}
          </span>
        </motion.div>

        {/* Category badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{categoryLabel}</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 sm:mt-6 font-semibold tracking-[-0.025em] text-ink-950 text-[clamp(1.9rem,4.4vw,3.2rem)] leading-[1.1]"
        >
          {meta.title}
        </motion.h1>

        {/* Excerpt */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-5 text-base sm:text-lg text-ink-600 leading-relaxed"
        >
          {meta.excerpt}
        </motion.p>

        {/* Meta row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-ink-500 pb-6 border-b border-ink-100"
        >
          <span className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
            {t('blog.byline', { name: article.author })}
          </span>
          <span className="text-ink-300">·</span>
          <span>{formattedDate}</span>
          <span className="text-ink-300">·</span>
          <span>{t('blog.readTime', { n: article.readTime })}</span>
        </motion.div>

        {/* Cover */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
          className="mt-8 rounded-2xl sm:rounded-3xl overflow-hidden border border-ink-100 aspect-[16/9]"
        >
          <ArticleCover cover={article.cover} className="w-full h-full" />
        </motion.div>

        {/* Article body */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="article-prose mt-12 sm:mt-16"
        >
          {/* Le repli ne s'affiche qu'une fois le contenu résolu : rendre un
              autre élément pendant le chargement changerait la structure entre
              le HTML prérendu et le premier rendu client (React #418). */}
          {ready && !content ? (
            <p className="text-ink-500 italic">
              {t('blog.article.contentUnavailable')}
            </p>
          ) : (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </motion.article>

        {/* ───────────── FINAL CTA — About-style dark dramatic ───────────── */}
        <section className="relative mt-20 sm:mt-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl bg-gradient-to-br from-ink-950 via-ink-900 to-ink-950 text-white px-6 sm:px-12 py-14 sm:py-20 overflow-hidden isolate"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 -z-10"
              style={{
                backgroundImage:
                  'radial-gradient(rgba(255,255,255,0.18) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
                maskImage:
                  'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full blur-3xl -z-10"
              style={{
                background:
                  'radial-gradient(circle, rgba(59,93,255,0.55), transparent 70%)',
              }}
            />
            <motion.div
              aria-hidden
              animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{
                duration: 11,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 2,
              }}
              className="absolute -left-32 -bottom-32 w-[380px] h-[380px] rounded-full blur-3xl -z-10"
              style={{
                background:
                  'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)',
              }}
            />

            <div className="relative max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm px-3 py-1 text-xs font-semibold text-accent-200">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-70 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
                </span>
                {t('blog.article.ctaBadge')}
              </span>

              <h3 className="mt-5 font-semibold tracking-[-0.025em] text-[clamp(1.8rem,4.5vw,3rem)] leading-[1.1]">
                {t('blog.article.ctaTitleLead')}{' '}
                <span className="font-semibold bg-gradient-to-r from-accent-300 via-accent-200 to-white bg-clip-text text-transparent pb-[0.12em] pr-[0.08em] inline-block">
                  {t('blog.article.ctaTitleHighlight')}
                </span>{' '}
                {t('blog.article.ctaTitleTail')}
              </h3>

              <p className="mt-4 text-base text-ink-300 leading-relaxed max-w-xl">
                {t('blog.article.ctaCopy')}
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`${localizedPath('contact', locale)}#rendez-vous`}
                  className="relative group inline-flex items-center gap-2 bg-ink-100 hover:bg-accent-50 text-ink-950 rounded-full pl-5 pr-4 py-3 text-sm font-semibold shadow-[0_24px_60px_-18px_rgba(59,93,255,0.7)] transition-colors overflow-hidden"
                >
                  <span
                    aria-hidden
                    className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-accent-200/60 to-transparent pointer-events-none"
                  />
                  <span className="relative">{t('blog.article.ctaPrimary')}</span>
                  <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-ink-950 text-white">
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <path
                        d="M5 12h14M13 5l7 7-7 7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Link>
                <Link
                  to={localizedPath('blog', locale)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur px-5 py-3 text-sm font-medium text-white/90 hover:bg-white/10 hover:border-white/30 transition-colors"
                >
                  {t('blog.article.ctaSecondary')}
                </Link>
              </div>

              <div className="mt-9 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-400">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  {t('blog.article.trust1')}
                </span>
                <span className="flex items-center gap-2">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    className="text-accent-400"
                  >
                    <path
                      d="M5 12l5 5L20 7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {t('blog.article.trust2')}
                </span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Related articles */}
        {related.length > 0 && (
          <section className="mt-16 sm:mt-20">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-ink-500">
              {t('blog.article.relatedHeading')}
            </p>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 sm:gap-4">
              {related.map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                >
                  <Link
                    to={localizedPath('blog.article', locale, { slug: articleSlug(r, locale) })}
                    className="group block relative rounded-2xl border border-ink-100 bg-ink-100 p-5 hover:border-accent-200 hover:shadow-[0_18px_40px_-22px_rgba(59,93,255,0.4)] transition-all overflow-hidden h-full"
                  >
                    <span
                      aria-hidden
                      className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl pointer-events-none opacity-0 group-hover:opacity-60 transition-opacity"
                      style={{
                        background:
                          'radial-gradient(circle, rgba(59,93,255,0.35), transparent 70%)',
                      }}
                    />
                    <div className="relative">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-accent-700">
                        {t('blog.categories.' + r.category)}
                      </p>
                      <h4 className="mt-2 text-sm sm:text-base font-semibold text-ink-950 tracking-tight line-clamp-3">
                        {r.i18n[locale].title}
                      </h4>
                      <p className="mt-2 text-xs text-ink-500">
                        {t('blog.readTimeShort', { n: r.readTime })} · {formatDateLocalized(r.date, locale)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
    </>
  )
}
